import { ISchema, PaywallComponentProps } from '../../../@types'
import { mergeDeep } from '@/helpers/utils'

// Gift-box ("price before scratch card") treatment for the `activity-female-yoga` funnel
// (base paywall: instances/areas/yoga/yoga-f.ts). This base already uses PaywallPlanPickerComp, so
// no static→Comp swap is needed — we only enable the gift-box config and append the modal.
//
// The base Comp's per-day price already branches on removeDiscount (full price before the box is
// picked), but its 4-week / 12-week *subtitle* shows the discounted `{trial_price}` even before the
// pick. We override the Comp to show the full `{price}` there instead, so the reveal matches the
// other gift-box funnels (walking-f / chair-tai-chi-m). The 1-week plan already shows `{trial_price}`
// in both states by design, so it is unchanged.
export default (schema: ISchema): ISchema =>
	mergeDeep(
		{ ...schema },
		{
			config: {
				giftBoxModal: true,
				initialDiscount: false,
				countdown: { autoStart: false },
			},
			order: [...schema.order, 'PaywallGiftBoxModal'],
			components: {
				PaywallPlanPickerComp: ({ ctx, $t }): PaywallComponentProps.PlanPickerComp => ({
					items: [
						{
							title: {
								text: $t('female_yoga.pages.paywall.plans.1_week.plan_name'),
								uppercase: true,
							},
							subtitle: {
								text: ctx.removeDiscount ? '{trial_price}' : `<s>{original_trial_price}</s> {trial_price}`,
								color: 'secondary',
								badge: {
									text: $t('female_yoga.pages.paywall.plans.1_week.sub_name'),
									uppercase: true,
								},
							},
							price: {
								template: 'labeled-price',
								extras: {
									originalPrice: ctx.removeDiscount ? '' : '{original_trial_price_per_day_calculated}',
									dollars: '{trial_price_per_day_dollars}',
									cents: '{trial_price_per_day_cents}',
									period: $t('pages.program.cheap_and_short.common.planPicker.perDay'),
								},
							},
						},
						{
							title: {
								text: $t('female_yoga.pages.paywall.plans.4_weeks.plan_name'),
								uppercase: true,
							},
							badge: {
								text: $t('pages.program.cheap_and_short.common.planPicker.mostPopular'),
								uppercase: true,
							},
							subtitle: {
								text: ctx.removeDiscount ? '{price}' : '<s>{price}</s> {trial_price}',
								color: 'secondary',
							},
							price: {
								template: 'labeled-price',
								extras: {
									originalPrice: ctx.removeDiscount ? '' : '{original_trial_price_per_day_calculated}',
									dollars: ctx.removeDiscount ? '{price_per_day_dollars}' : '{trial_price_per_day_dollars}',
									cents: ctx.removeDiscount ? '{price_per_day_cents}' : '{trial_price_per_day_cents}',
									period: $t('pages.program.cheap_and_short.common.planPicker.perDay'),
								},
							},
						},
						{
							title: {
								text: $t('female_yoga.pages.paywall.plans.12_weeks.plan_name'),
								uppercase: true,
							},
							subtitle: {
								text: ctx.removeDiscount ? '{price}' : '<s>{price}</s> {trial_price}',
								color: 'secondary',
							},
							price: {
								template: 'labeled-price',
								extras: {
									originalPrice: ctx.removeDiscount ? '' : '{original_trial_price_per_day_calculated}',
									dollars: ctx.removeDiscount ? '{price_per_day_dollars}' : '{trial_price_per_day_dollars}',
									cents: ctx.removeDiscount ? '{price_per_day_cents}' : '{trial_price_per_day_cents}',
									period: $t('pages.program.cheap_and_short.common.planPicker.perDay'),
								},
							},
						},
					],
					bulletType: 'dot',
				}),
				PaywallGiftBoxModal: ({ ctx }): PaywallComponentProps.GiftBoxModal => ({
					discountPercent: ctx.giftBoxDiscountPercent,
				}),
			},
		},
	)
